import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Shield,
  Users,
  FileText,
  Link2,
  Plus,
  Search,
  Eye,
  Download,
  CheckCircle,
  Clock,
  DollarSign,
  TrendingUp,
  LogOut,
  AlertCircle,
  RefreshCw,
  Copy,
  ExternalLink,
  ShoppingCart,
  Mail,
} from 'lucide-react';
import { Container } from '../../components/layout/Container';
import { Button } from '../../components/ui/Button';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import { Modal } from '../../components/ui/Modal';
import { Spinner } from '../../components/ui/Spinner';
import { useToast } from '../../contexts/ToastContext';
import { cn } from '../../lib/utils';
import {
  adminLogout,
  getAdminDashboard,
  listSnapshots,
  createInvite,
  listInvites,
  isAdminLoggedIn,
  getAdminSession,
  getPendingReports,
  releasePdfReport,
  triggerPdfGeneration,
  getSnapshotDetails,
  type DashboardData,
  type SnapshotListItem,
  type PendingReport,
} from '../../lib/api';

type Tab = 'overview' | 'snapshots' | 'pending' | 'invites' | 'settings';

interface Invite {
  id: string;
  tokenPrefix: string;
  status: string;
  createdAt: Date | null;
  expiresAt: Date | null;
  createdBy: string;
  accessCount: number;
}

export function AdminDashboard() {
  const navigate = useNavigate();
  const { success: showSuccess, error: showError } = useToast();
  const [activeTab, setActiveTab] = useState<Tab>('overview');
  const [searchQuery, setSearchQuery] = useState('');
  const [showNewInviteModal, setShowNewInviteModal] = useState(false);
  const [isCreatingInvite, setIsCreatingInvite] = useState(false);
  const [newInviteUrl, setNewInviteUrl] = useState<string | null>(null);
  const [selectedSnapshot, setSelectedSnapshot] = useState<{
    id: string;
    agencyName: string;
    status: string;
    riskBand?: string;
    totalScore?: number;
    email?: string;
    createdAt?: string;
  } | null>(null);

  // Data states
  const [isLoading, setIsLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [snapshots, setSnapshots] = useState<SnapshotListItem[]>([]);
  const [pendingReports, setPendingReports] = useState<PendingReport[]>([]);
  const [invites, setInvites] = useState<Invite[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [releasingId, setReleasingId] = useState<string | null>(null);
  const [generatingPdfId, setGeneratingPdfId] = useState<string | null>(null);

  // Check authentication on mount
  useEffect(() => {
    if (!isAdminLoggedIn()) {
      navigate('/admin/login');
      return;
    }
    loadDashboardData();
  }, [navigate]);

  const loadDashboardData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await getAdminDashboard();
      setDashboardData(data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load dashboard';
      if (message.includes('session') || message.includes('authenticated')) {
        navigate('/admin/login');
        return;
      }
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  const loadSnapshots = async () => {
    try {
      const data = await listSnapshots();
      setSnapshots(data);
    } catch (err) {
      showError('Failed to load snapshots');
    }
  };

  const loadPendingReports = async () => {
    try {
      const data = await getPendingReports();
      setPendingReports(data);
    } catch (err) {
      console.error('Failed to load pending reports:', err);
    }
  };

  const handleReleasePdf = async (snapshotId: string, sendEmail: boolean = true) => {
    setReleasingId(snapshotId);
    try {
      const result = await releasePdfReport(snapshotId, sendEmail);
      if (result.success) {
        showSuccess(result.emailSent ? 'PDF released and email sent!' : 'PDF released!');
        loadPendingReports();
        loadSnapshots();
      }
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to release PDF');
    } finally {
      setReleasingId(null);
    }
  };

  const handleGeneratePdf = async (snapshotId: string) => {
    setGeneratingPdfId(snapshotId);
    try {
      const result = await triggerPdfGeneration(snapshotId);
      if (result.success) {
        showSuccess('PDF generation queued!');
        setTimeout(() => loadPendingReports(), 3000);
      }
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to generate PDF');
    } finally {
      setGeneratingPdfId(null);
    }
  };

  const loadInvites = async () => {
    try {
      const data = await listInvites();
      setInvites(data.map(inv => ({
        id: inv.id,
        tokenPrefix: inv.tokenPrefix,
        status: inv.status,
        createdAt: inv.createdAt ? new Date(inv.createdAt) : null,
        expiresAt: inv.expiresAt ? new Date(inv.expiresAt) : null,
        createdBy: inv.createdBy,
        accessCount: inv.accessCount,
      })));
    } catch (err) {
      showError('Failed to load invites');
    }
  };

  // Load data when switching tabs
  useEffect(() => {
    if (activeTab === 'snapshots' && snapshots.length === 0) {
      loadSnapshots();
    }
    if (activeTab === 'pending') {
      loadPendingReports();
    }
    if (activeTab === 'invites' && invites.length === 0) {
      loadInvites();
    }
  }, [activeTab]);

  const handleLogout = async () => {
    try {
      await adminLogout();
      showSuccess('Logged out successfully');
    } catch {
      // Ignore errors
    }
    navigate('/admin/login');
  };

  const handleCreateInvite = async () => {
    setIsCreatingInvite(true);
    try {
      const result = await createInvite(14);

      // Show the URL in modal
      setNewInviteUrl(result.inviteUrl);
      setShowNewInviteModal(false);

      // Reload invites list
      await loadInvites();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create invite';
      showError(message);
    } finally {
      setIsCreatingInvite(false);
    }
  };

  const handleCopyInviteUrl = async () => {
    if (newInviteUrl) {
      await navigator.clipboard.writeText(newInviteUrl);
      showSuccess('Invite link copied to clipboard!');
    }
  };

  const handleViewSnapshot = async (snapshot: SnapshotListItem) => {
    // Set basic info immediately
    setSelectedSnapshot(snapshot);

    // Fetch full details to get email
    try {
      const details = await getSnapshotDetails(snapshot.id);
      const fullSnapshot = details.snapshot as Record<string, unknown>;
      const agencyProfile = fullSnapshot.agencyProfile as Record<string, unknown> | undefined;

      setSelectedSnapshot({
        ...snapshot,
        email: (agencyProfile?.contactEmail as string) || snapshot.email,
      });
    } catch (err) {
      console.error('Failed to fetch snapshot details:', err);
    }
  };

  const handleDownloadPdf = async (snapshotId: string) => {
    try {
      const details = await getSnapshotDetails(snapshotId);
      const snapshot = details.snapshot as Record<string, unknown>;
      const pdf = snapshot.pdf as Record<string, unknown> | undefined;

      if (pdf?.downloadUrl) {
        window.open(pdf.downloadUrl as string, '_blank');
      } else {
        showError('PDF not available. Generate it first from Held Reports.');
      }
    } catch (err) {
      showError('Failed to get PDF download URL');
    }
  };

  const tabs = [
    { id: 'overview', label: 'Overview', shortLabel: 'Home', icon: TrendingUp },
    { id: 'snapshots', label: 'Snapshots', shortLabel: 'Snaps', icon: FileText },
    { id: 'pending', label: 'Held Reports', shortLabel: 'Held', icon: Clock },
    { id: 'invites', label: 'Invites', shortLabel: 'Invites', icon: Link2 },
    { id: 'settings', label: 'Settings', shortLabel: 'Settings', icon: Users },
  ];

  const session = getAdminSession();
  const activeInvitesCount = invites.filter(i => i.status === 'active').length;
  const stats = dashboardData
    ? [
        { label: 'Total Snapshots', value: dashboardData.stats.total, icon: FileText },
        { label: 'Completed', value: dashboardData.stats.completed, icon: CheckCircle },
        { label: 'In Progress', value: (dashboardData.stats.paid || 0) + (dashboardData.stats.preview || 0), icon: DollarSign },
        { label: 'Active Invites', value: dashboardData.stats.activeInvites, icon: Link2 },
      ]
    : [];

  const purchaseChannelStats = dashboardData
    ? [
        { label: 'Direct Signups', value: dashboardData.stats.directPurchases || 0, icon: ShoppingCart },
        { label: 'Via Invite Links', value: dashboardData.stats.inviteBasedPurchases || 0, icon: Mail },
      ]
    : [];

  // Filter snapshots by search
  const filteredSnapshots = snapshots.filter(s =>
    s.agencyName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (s.email || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <Spinner size="lg" className="mx-auto mb-4" />
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <Card className="max-w-md w-full mx-4">
          <CardContent className="p-6 text-center">
            <AlertCircle className="w-12 h-12 mx-auto mb-4 text-red-500" />
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Error Loading Dashboard</h2>
            <p className="text-gray-600 mb-4">{error}</p>
            <Button onClick={loadDashboardData} leftIcon={<RefreshCw className="w-4 h-4" />}>
              Try Again
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-gray-900 text-white">
        <Container>
          <div className="flex items-center justify-between h-14 sm:h-16">
            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
              <div className="w-8 h-8 bg-teal-600 rounded-lg flex items-center justify-center flex-shrink-0">
                <Shield className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <span className="font-bold text-sm sm:text-base">Snapshot Admin</span>
                <span className="hidden sm:inline text-gray-400 text-sm ml-2">{session?.admin.email}</span>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="text-gray-300 hover:text-white hover:bg-gray-800 flex-shrink-0"
              onClick={handleLogout}
              leftIcon={<LogOut className="w-4 h-4" />}
            >
              <span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
        </Container>
      </header>

      {/* Tabs */}
      <div className="bg-white border-b overflow-x-auto">
        <Container>
          <nav className="flex gap-1 sm:gap-4">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as Tab)}
                className={cn(
                  'flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-3 font-medium border-b-2 transition-colors text-xs sm:text-base whitespace-nowrap',
                  activeTab === tab.id
                    ? 'border-teal-600 text-teal-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                )}
              >
                <tab.icon className="w-4 h-4" />
                <span className="sm:hidden">{tab.shortLabel}</span>
                <span className="hidden sm:inline">{tab.label}</span>
              </button>
            ))}
          </nav>
        </Container>
      </div>

      <main className="py-4 sm:py-8">
        <Container>
          {/* Overview Tab */}
          {activeTab === 'overview' && dashboardData && (
            <div className="space-y-6 sm:space-y-8">
              {/* Stats Grid */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                {stats.map(stat => (
                  <Card key={stat.label}>
                    <CardContent className="p-4 sm:p-6">
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-xs sm:text-sm text-gray-500 truncate">{stat.label}</p>
                          <p className="text-xl sm:text-3xl font-bold text-gray-900 mt-1">
                            {stat.value}
                          </p>
                        </div>
                        <div className="w-10 h-10 sm:w-12 sm:h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <stat.icon className="w-5 h-5 sm:w-6 sm:h-6 text-teal-600" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Signup Channels */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base sm:text-lg">Signup Channels</CardTitle>
                </CardHeader>
                <CardContent className="p-4 sm:p-6 pt-0">
                  <div className="grid grid-cols-2 gap-3 sm:gap-4">
                    {purchaseChannelStats.map(stat => (
                      <div key={stat.label} className="flex items-center gap-3 p-3 sm:p-4 bg-gray-50 rounded-lg">
                        <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <stat.icon className="w-5 h-5 text-teal-600" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-lg sm:text-2xl font-bold text-gray-900">{stat.value}</p>
                          <p className="text-xs sm:text-sm text-gray-500 truncate">{stat.label}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-3">
                    Direct signups come from agencies who start the assessment directly. Invite-based signups come from agencies who received an invite link.
                  </p>
                </CardContent>
              </Card>

              {/* Recent Activity */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base sm:text-lg">Recent Snapshots</CardTitle>
                </CardHeader>
                <CardContent className="p-3 sm:p-6">
                  {dashboardData.recentSnapshots.length === 0 ? (
                    <p className="text-gray-500 text-center py-8">No snapshots yet</p>
                  ) : (
                    <div className="space-y-3 sm:space-y-4">
                      {dashboardData.recentSnapshots.map(snapshot => (
                        <div
                          key={snapshot.id}
                          className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 sm:p-4 bg-gray-50 rounded-lg gap-3"
                        >
                          <div className="flex items-center gap-3 sm:gap-4">
                            <div
                              className={cn(
                                'w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center flex-shrink-0',
                                snapshot.status === 'completed'
                                  ? 'bg-green-100'
                                  : snapshot.status === 'in_progress'
                                  ? 'bg-yellow-100'
                                  : 'bg-blue-100'
                              )}
                            >
                              {snapshot.status === 'completed' ? (
                                <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
                              ) : snapshot.status === 'in_progress' ? (
                                <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-600" />
                              ) : (
                                <DollarSign className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                              )}
                            </div>
                            <div className="min-w-0">
                              <p className="font-medium text-gray-900 text-sm sm:text-base truncate">
                                {snapshot.agencyName}
                              </p>
                              {snapshot.email && (
                                <p className="text-xs text-teal-600 truncate">
                                  {snapshot.email}
                                </p>
                              )}
                              <p className="text-xs sm:text-sm text-gray-500">
                                {snapshot.createdAt
                                  ? new Date(snapshot.createdAt).toLocaleDateString()
                                  : 'Unknown date'}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 sm:gap-4 ml-11 sm:ml-0">
                            {snapshot.totalScore !== undefined && (
                              <span
                                className={cn(
                                  'px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium',
                                  snapshot.riskBand === 'low'
                                    ? 'bg-green-100 text-green-700'
                                    : snapshot.riskBand === 'medium'
                                    ? 'bg-yellow-100 text-yellow-700'
                                    : 'bg-red-100 text-red-700'
                                )}
                              >
                                Score: {snapshot.totalScore}
                              </span>
                            )}
                            <Button variant="ghost" size="sm" onClick={() => handleViewSnapshot(snapshot)}>
                              <Eye className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* Snapshots Tab */}
          {activeTab === 'snapshots' && (
            <Card>
              <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 sm:p-6">
                <CardTitle className="text-base sm:text-lg">All Snapshots</CardTitle>
                <div className="flex gap-2">
                  <div className="relative flex-1 sm:flex-none">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search..."
                      value={searchQuery}
                      onChange={e => setSearchQuery(e.target.value)}
                      className="w-full sm:w-auto pl-10 pr-4 py-2 border rounded-lg text-sm"
                    />
                  </div>
                  <Button variant="ghost" size="sm" onClick={loadSnapshots} className="flex-shrink-0">
                    <RefreshCw className="w-4 h-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-0 sm:p-6 sm:pt-0">
                {filteredSnapshots.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">
                    {searchQuery ? 'No matching snapshots' : 'No snapshots yet'}
                  </p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[500px]">
                      <thead>
                        <tr className="text-left text-xs sm:text-sm text-gray-500 border-b">
                          <th className="pb-3 pl-4 sm:pl-0 font-medium">Agency</th>
                          <th className="pb-3 font-medium">Email</th>
                          <th className="pb-3 font-medium">Status</th>
                          <th className="pb-3 font-medium">Score</th>
                          <th className="pb-3 font-medium">Created</th>
                          <th className="pb-3 pr-4 sm:pr-0 font-medium">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {filteredSnapshots.map(snapshot => (
                          <tr key={snapshot.id} className="text-sm">
                            <td className="py-3 sm:py-4 pl-4 sm:pl-0 font-medium text-gray-900 max-w-[150px] truncate">
                              {snapshot.agencyName}
                            </td>
                            <td className="py-3 sm:py-4 text-gray-600 text-xs max-w-[200px] truncate">
                              {snapshot.email || '—'}
                            </td>
                            <td className="py-3 sm:py-4">
                              <span
                                className={cn(
                                  'px-2 py-1 rounded text-xs font-medium whitespace-nowrap',
                                  snapshot.status === 'completed'
                                    ? 'bg-green-100 text-green-700'
                                    : snapshot.status === 'in_progress'
                                    ? 'bg-yellow-100 text-yellow-700'
                                    : 'bg-blue-100 text-blue-700'
                                )}
                              >
                                {snapshot.status}
                              </span>
                            </td>
                            <td className="py-3 sm:py-4 text-gray-600">
                              {snapshot.totalScore ?? '—'}
                            </td>
                            <td className="py-3 sm:py-4 text-gray-600 whitespace-nowrap">
                              {snapshot.createdAt
                                ? new Date(snapshot.createdAt).toLocaleDateString()
                                : '—'}
                            </td>
                            <td className="py-3 sm:py-4 pr-4 sm:pr-0">
                              <div className="flex gap-1">
                                <Button variant="ghost" size="sm" onClick={() => handleViewSnapshot(snapshot)}>
                                  <Eye className="w-4 h-4" />
                                </Button>
                                {snapshot.status === 'completed' && (
                                  <Button variant="ghost" size="sm" onClick={() => handleDownloadPdf(snapshot.id)}>
                                    <Download className="w-4 h-4" />
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Pending Reports Tab */}
          {activeTab === 'pending' && (
            <Card>
              <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 sm:p-6">
                <div>
                  <CardTitle className="text-base sm:text-lg">Held Reports · Needs Attention</CardTitle>
                  <p className="text-sm text-gray-500 mt-1">
                    Reports now release instantly. Anything here couldn't auto-send — usually a missing email — and needs a quick fix ({pendingReports.length}).
                  </p>
                </div>
                <Button variant="ghost" size="sm" onClick={loadPendingReports} className="flex-shrink-0">
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </CardHeader>
              <CardContent className="p-0">
                {pendingReports.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">
                    <Clock className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>Nothing needs attention</p>
                    <p className="text-sm mt-1">All reports released instantly — no stuck reports</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-gray-50">
                          <th className="text-left p-3 font-medium text-gray-600">Agency</th>
                          <th className="text-left p-3 font-medium text-gray-600">Email</th>
                          <th className="text-left p-3 font-medium text-gray-600">Risk</th>
                          <th className="text-left p-3 font-medium text-gray-600">Completed</th>
                          <th className="text-left p-3 font-medium text-gray-600">Auto-Release In</th>
                          <th className="text-right p-3 font-medium text-gray-600">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pendingReports.map((report) => (
                          <tr key={report.id} className="border-b hover:bg-gray-50">
                            <td className="p-3 font-medium">
                              {report.agencyName}
                              {(() => {
                                const reason = (report as { holdReason?: string }).holdReason;
                                const tag = reason === 'email_missing'
                                  ? { label: 'Missing email', cls: 'bg-red-100 text-red-700' }
                                  : reason === 'generation_error'
                                    ? { label: 'Generation error', cls: 'bg-orange-100 text-orange-700' }
                                    : reason === 'admin_hold'
                                      ? { label: 'Admin hold', cls: 'bg-purple-100 text-purple-700' }
                                      : reason && reason !== 'other'
                                        ? { label: reason, cls: 'bg-gray-100 text-gray-600' }
                                        : null;
                                return tag ? (
                                  <span className={cn('ml-2 px-1.5 py-0.5 rounded text-[11px] font-medium align-middle', tag.cls)}>
                                    {tag.label}
                                  </span>
                                ) : null;
                              })()}
                            </td>
                            <td className="p-3 text-gray-600">{report.email || 'Not provided'}</td>
                            <td className="p-3">
                              <span className={cn(
                                'px-2 py-1 rounded text-xs font-medium',
                                report.riskBand === 'low' ? 'bg-green-100 text-green-700' :
                                report.riskBand === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                                'bg-red-100 text-red-700'
                              )}>
                                {report.riskBand} ({report.totalScore})
                              </span>
                            </td>
                            <td className="p-3 text-gray-600">
                              {new Date(report.completedAt).toLocaleString()}
                            </td>
                            <td className="p-3 text-gray-600">
                              <div className="flex flex-col">
                                <span className={cn(
                                  'text-xs px-1.5 py-0.5 rounded mb-1 inline-block w-fit',
                                  report.hasPdf ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                                )}>
                                  {report.hasPdf ? 'PDF Ready' : 'No PDF'}
                                </span>
                                <span className="text-xs">
                                  {report.hoursUntilAutoRelease > 0
                                    ? `Auto: ${report.hoursUntilAutoRelease}h`
                                    : 'Ready'}
                                </span>
                              </div>
                            </td>
                            <td className="p-3 text-right">
                              <div className="flex justify-end gap-2">
                                {!report.hasPdf && (
                                  <Button
                                    size="sm"
                                    variant="secondary"
                                    onClick={() => handleGeneratePdf(report.id)}
                                    disabled={generatingPdfId === report.id}
                                  >
                                    {generatingPdfId === report.id ? (
                                      <Spinner className="w-4 h-4" />
                                    ) : (
                                      <FileText className="w-4 h-4" />
                                    )}
                                    <span className="ml-1 hidden sm:inline">Generate PDF</span>
                                  </Button>
                                )}
                                {report.hasPdf && (
                                  <Button
                                    size="sm"
                                    onClick={() => handleReleasePdf(report.id, true)}
                                    disabled={releasingId === report.id}
                                  >
                                    {releasingId === report.id ? (
                                      <Spinner className="w-4 h-4" />
                                    ) : (
                                      <Mail className="w-4 h-4" />
                                    )}
                                    <span className="ml-1 hidden sm:inline">Release & Email</span>
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Invites Tab */}
          {activeTab === 'invites' && (
            <div className="space-y-4 sm:space-y-6">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                <div>
                  <h2 className="text-lg sm:text-xl font-semibold text-gray-900">
                    Invite Links
                  </h2>
                  <p className="text-sm text-gray-500">
                    {activeInvitesCount} active • {invites.length} total
                  </p>
                </div>
                <Button
                  leftIcon={<Plus className="w-4 h-4" />}
                  onClick={() => setShowNewInviteModal(true)}
                  className="w-full sm:w-auto"
                >
                  Create Invite
                </Button>
              </div>

              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-blue-800 text-sm">
                  <strong>How it works:</strong> Create an invite link → Share with agency → They visit link → Snapshot is created → They complete assessment
                </p>
              </div>

              <Card>
                <CardContent className="p-0">
                  {invites.length === 0 ? (
                    <div className="p-6 sm:p-8 text-center">
                      <Link2 className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-4 text-gray-300" />
                      <p className="text-gray-500 mb-4 text-sm sm:text-base">No invites created yet</p>
                      <Button
                        leftIcon={<Plus className="w-4 h-4" />}
                        onClick={() => setShowNewInviteModal(true)}
                      >
                        Create Your First Invite
                      </Button>
                    </div>
                  ) : (
                    <div className="divide-y">
                      {invites.map(invite => (
                        <div key={invite.id} className="p-3 sm:p-4 flex items-center justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-mono text-xs sm:text-sm text-gray-600">
                                {invite.tokenPrefix}...
                              </span>
                              <span className={cn(
                                'px-2 py-0.5 rounded text-xs font-medium flex-shrink-0',
                                invite.status === 'active'
                                  ? 'bg-green-100 text-green-700'
                                  : invite.status === 'expired'
                                  ? 'bg-gray-100 text-gray-600'
                                  : 'bg-blue-100 text-blue-700'
                              )}>
                                {invite.status}
                              </span>
                            </div>
                            <p className="text-xs text-gray-500">
                              {invite.expiresAt
                                ? `Expires ${invite.expiresAt.toLocaleDateString()}`
                                : 'No expiry'
                              }
                              {invite.accessCount > 0 && (
                                <span className="ml-2">• {invite.accessCount} access{invite.accessCount !== 1 ? 'es' : ''}</span>
                              )}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* New Invite Modal */}
              <Modal
                isOpen={showNewInviteModal}
                onClose={() => setShowNewInviteModal(false)}
                title="Create New Invite"
              >
                <div className="space-y-4">
                  <p className="text-gray-600 text-sm sm:text-base">
                    Create a new invite link that expires in 14 days.
                    The link will be automatically copied to your clipboard.
                  </p>
                  <div className="flex flex-col-reverse sm:flex-row gap-2 sm:gap-3">
                    <Button
                      variant="ghost"
                      onClick={() => setShowNewInviteModal(false)}
                      className="w-full sm:w-auto"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleCreateInvite}
                      isLoading={isCreatingInvite}
                      className="w-full sm:flex-1"
                    >
                      Create Invite
                    </Button>
                  </div>
                </div>
              </Modal>
            </div>
          )}

          {/* Settings Tab */}
          {activeTab === 'settings' && (
            <Card>
              <CardHeader>
                <CardTitle>Admin Settings</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <h3 className="font-medium text-gray-900 mb-2">Account Info</h3>
                    <p className="text-sm text-gray-600">
                      <strong>Email:</strong> {session?.admin.email}
                    </p>
                    <p className="text-sm text-gray-600">
                      <strong>Role:</strong> {session?.admin.role}
                    </p>
                  </div>
                  <p className="text-gray-500 text-sm">
                    Additional settings coming soon. Contact support@getasili.com for admin changes.
                  </p>
                </div>
              </CardContent>
            </Card>
          )}
        </Container>
      </main>

      {/* Invite Created Modal */}
      <Modal
        isOpen={!!newInviteUrl}
        onClose={() => setNewInviteUrl(null)}
        title="Invite Created!"
      >
        <div className="space-y-4">
          <p className="text-gray-600 text-sm">
            Share this link with an agency. When they visit, they'll be able to complete the compliance assessment.
          </p>
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-amber-800 text-xs font-medium">
              ⚠️ Copy this link now! For security, the full link cannot be retrieved after closing this modal.
            </p>
          </div>
          <div className="flex items-center gap-2 p-3 bg-gray-100 rounded-lg border">
            <input
              type="text"
              value={newInviteUrl || ''}
              readOnly
              className="flex-1 bg-transparent text-sm text-gray-700 outline-none font-mono"
              onClick={(e) => (e.target as HTMLInputElement).select()}
            />
            <Button size="sm" onClick={handleCopyInviteUrl} leftIcon={<Copy className="w-4 h-4" />}>
              Copy
            </Button>
          </div>
          <p className="text-xs text-gray-500">
            This link expires in 14 days. A snapshot will be created when the recipient visits this link.
          </p>
          <div className="flex flex-col sm:flex-row gap-2 pt-2">
            <Button
              variant="ghost"
              onClick={() => setNewInviteUrl(null)}
              className="w-full sm:w-auto"
            >
              Done
            </Button>
            <Button
              onClick={() => {
                if (newInviteUrl) window.open(newInviteUrl, '_blank');
              }}
              leftIcon={<ExternalLink className="w-4 h-4" />}
              className="w-full sm:flex-1"
            >
              Test Link
            </Button>
          </div>
        </div>
      </Modal>

      {/* Snapshot Details Modal */}
      <Modal
        isOpen={!!selectedSnapshot}
        onClose={() => setSelectedSnapshot(null)}
        title="Snapshot Details"
        size="lg"
      >
        {selectedSnapshot && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div>
                <p className="text-xs text-gray-500 uppercase">Agency Name</p>
                <p className="font-medium text-gray-900 text-sm sm:text-base break-words">
                  {selectedSnapshot.agencyName || 'Not provided'}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase">Status</p>
                <span
                  className={cn(
                    'inline-block px-2 py-1 rounded text-xs font-medium',
                    selectedSnapshot.status === 'completed'
                      ? 'bg-green-100 text-green-700'
                      : selectedSnapshot.status === 'in_progress'
                      ? 'bg-yellow-100 text-yellow-700'
                      : 'bg-blue-100 text-blue-700'
                  )}
                >
                  {selectedSnapshot.status}
                </span>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase">Risk Score</p>
                <p className="font-medium text-gray-900 text-sm sm:text-base">
                  {selectedSnapshot.totalScore ?? 'Not scored'}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase">Risk Band</p>
                <p className={cn(
                  'font-medium text-sm sm:text-base',
                  selectedSnapshot.riskBand === 'low' ? 'text-green-600' :
                  selectedSnapshot.riskBand === 'medium' ? 'text-yellow-600' :
                  selectedSnapshot.riskBand === 'high' ? 'text-red-600' : 'text-gray-600'
                )}>
                  {selectedSnapshot.riskBand ? selectedSnapshot.riskBand.charAt(0).toUpperCase() + selectedSnapshot.riskBand.slice(1) : 'N/A'}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase">Email</p>
                <p className="font-medium text-gray-900 text-sm sm:text-base break-all">
                  {selectedSnapshot.email || 'Not provided'}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase">Created</p>
                <p className="font-medium text-gray-900 text-sm sm:text-base">
                  {selectedSnapshot.createdAt
                    ? new Date(selectedSnapshot.createdAt).toLocaleString()
                    : 'Unknown'}
                </p>
              </div>
            </div>
            <div className="pt-4 border-t">
              <p className="text-xs text-gray-500 mb-2">Snapshot ID</p>
              <code className="text-xs bg-gray-100 px-2 py-1 rounded break-all block">
                {selectedSnapshot.id}
              </code>
            </div>
            <div className="flex justify-end">
              <Button variant="ghost" onClick={() => setSelectedSnapshot(null)}>
                Close
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
